import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Container, Paper, Avatar, Chip, Button,
  Skeleton, Divider,
} from '@mui/material';
import { LocationOn, AccessTime, Telegram, QrCode2, ReportProblem, Flag } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import groupsService, { GroupDetail } from '../../services/groupsService';
import userService from '../../services/userService';
import ReportAbuseModal from '../../components/shared/ReportAbuseModal';
import ReportHostModal from '../../components/shared/ReportHostModal';
import { colors } from '../../theme/theme';

export default function GroupDetailPage() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [group, setGroup] = useState<GroupDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [noGroup, setNoGroup] = useState(false);
  const [showAbuseReport, setShowAbuseReport] = useState(false);
  const [showHostReport, setShowHostReport] = useState(false);

  useEffect(() => {
    groupsService.getCurrent()
      .then(res => {
        if (res.group) setGroup(res.group);
        else setNoGroup(true);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  if (loading) return <Container maxWidth="md" sx={{ py: 4 }}><Skeleton variant="rounded" height={400} /></Container>;

  if (noGroup || !group) {
    return (
      <Container maxWidth="sm" sx={{ py: 6, textAlign: 'center' }}>
        <Typography variant="h1" sx={{ fontSize: '4rem', mb: 2 }}>⏳</Typography>
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{t('pages.group.no_active')}</Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          Your event hasn't been confirmed yet. Check back after the matching runs on Thursday!
        </Typography>
        <Button variant="contained" onClick={() => navigate('/vouchers')}>View My Vouchers</Button>
      </Container>
    );
  }

  const evt = group.event;
  const venue = group.venue;

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3 }}>{t('pages.group.your_table')} ☕</Typography>

      {/* Event Info */}
      {evt && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box sx={{ display: 'flex', gap: 1, mb: 1.5 }}>
            <Chip label={evt.event_type === 'professional' ? 'Professional' : 'Social'} size="small"
              sx={{ bgcolor: evt.event_type === 'professional' ? colors.primary.subtle : colors.accent.subtle,
                    color: evt.event_type === 'professional' ? colors.primary.main : colors.accent.dark, fontWeight: 600 }} />
            {evt.game_activity && <Chip label={`🎲 ${evt.game_activity}`} size="small" sx={{ bgcolor: colors.accent.subtle }} />}
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>{evt.title}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <AccessTime sx={{ fontSize: 18, color: colors.neutral[500] }} />
            <Typography color="text.secondary">
              {evt.scheduled_date} at {evt.scheduled_time?.slice(0, 5)} · {evt.duration_minutes} min
            </Typography>
          </Box>
          {evt.topic_brief && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1.5, fontStyle: 'italic' }}>
              "{evt.topic_brief}"
            </Typography>
          )}
        </Paper>
      )}

      {/* Venue */}
      {venue && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>📍 Venue</Typography>
          {venue.photo_url && (
            <Box sx={{ borderRadius: 3, overflow: 'hidden', mb: 2 }}>
              <img src={venue.photo_url} alt={venue.name} loading="lazy" style={{ width: '100%', height: 160, objectFit: 'cover' }} />
            </Box>
          )}
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{venue.name}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
            <LocationOn sx={{ fontSize: 16, color: colors.neutral[400] }} />
            <Typography variant="body2" color="text.secondary">{venue.address}</Typography>
          </Box>
        </Paper>
      )}

      {/* Telegram Link */}
      {group.telegram_link && (
        <Paper sx={{ p: 3, mb: 3, bgcolor: colors.secondary.subtle }}>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>💬 Group Chat</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Coordinate with your tablemates before the event!
          </Typography>
          <Button variant="contained" color="secondary" startIcon={<Telegram />}
            href={group.telegram_link} target="_blank" rel="noopener">
            Open Telegram Group
          </Button>
        </Paper>
      )}

      {/* Members */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
          👥 Your Tablemates ({group.member_count})
        </Typography>
        {group.members.map((m, i) => (
          <Box key={i}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1.5 }}>
              <Avatar src={m.avatar_url || undefined} sx={{ width: 48, height: 48, bgcolor: colors.primary.light }}>
                {m.first_name[0]}{m.last_name[0]}
              </Avatar>
              <Box sx={{ flex: 1 }}>
                <Typography sx={{ fontWeight: 600 }}>{m.first_name} {m.last_name}</Typography>
                {m.bio && <Typography variant="body2" color="text.secondary">{m.bio}</Typography>}
              </Box>
              {m.industry && (
                <Chip label={m.industry} size="small" sx={{ bgcolor: colors.neutral[100], fontSize: '0.7rem' }} />
              )}
              <Button size="small" variant="outlined" sx={{ ml: 'auto', minWidth: 80 }}
                onClick={(e) => { e.stopPropagation(); userService.sendConnection(`member-${i}`).catch(() => { /* no-op */ }); }}>
                Connect
              </Button>
            </Box>
            {i < group.members.length - 1 && <Divider />}
          </Box>
        ))}
      </Paper>

      {/* Actions */}
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="contained" fullWidth startIcon={<QrCode2 />}
          onClick={() => navigate('/vouchers')}>
          Show QR Code
        </Button>
      </Box>

      {/* Safety buttons */}
      <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
        <Button variant="outlined" color="error" size="small" startIcon={<ReportProblem />}
          onClick={() => setShowAbuseReport(true)}>Report Abuse</Button>
        <Button variant="outlined" color="warning" size="small" startIcon={<Flag />}
          onClick={() => setShowHostReport(true)}>Report Venue Issue</Button>
      </Box>

      {evt && (
        <>
          <ReportAbuseModal open={showAbuseReport} onClose={() => setShowAbuseReport(false)}
            eventId={evt.id} members={group.members.map((m, i) => ({ ...m, id: `member-${i}` }))} />
          {venue && (
            <ReportHostModal open={showHostReport} onClose={() => setShowHostReport(false)}
              eventId={evt.id} venueId={venue.id} venueName={venue.name} />
          )}
        </>
      )}
    </Container>
  );
}
